/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { FormSchema, FormStore, RequiredPath, ValidPath } from "@formisch/react"
import { Form, Field as FormField, getInput, reset, useForm } from "@formisch/react"
import { CircleAlertIcon, LockOpenIcon, TriangleAlertIcon } from "lucide-react"
import type { ComponentProps } from "react"
import { useState } from "react"
import * as v from "valibot"

import IapLogo from "../../assets/images/logo-iap.svg?react"
import type { Credentials } from "../../features/session/login-schema"
import { CredentialsSchema, TotpSchema } from "../../features/session/login-schema"
import { LoginStatus } from "../../features/session/session-types"
import { SessionViewModel } from "../../features/session/session-view-model"
import { forceHomeRedirect } from "../../utils/force-redirect"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { useViewModel } from "../app/app-hooks"
import { BusyButton } from "../components/busy-button"
import { PasswordInput } from "../components/password-input"
import { Alert, AlertTitle } from "../primitives/alert"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"
import { Collapsible, CollapsibleContent } from "../primitives/collapsible"
import { Field, FieldGroup, FieldLabel } from "../primitives/field"
import { Input } from "../primitives/input"

import styles from "./login-view.module.css"

/**
 * The login page. The user signs in with a username and a password, and the
 * TOTP form replaces them when the server asks for a second step.
 *
 * Both forms are created here, because a failed attempt resets both and starts
 * over from the credentials.
 */
export const LoginView = () => {
    // Held once the server asks for a TOTP code, because the second attempt sends them again.
    const [credentials, setCredentials] = useState<Credentials | null>(null)
    // The message outlives `open`, so the alert keeps its text while it animates out.
    const [error, setError] = useState({ message: "", open: false })

    const closeError = () => setError((current) => (current.open ? { ...current, open: false } : current))

    const vm = useViewModel(SessionViewModel)

    // A field is checked when it loses focus, then on every keystroke while it holds an error.
    const credentialsForm = useForm({ schema: CredentialsSchema, validate: "blur" })
    const totpForm = useForm({ schema: TotpSchema, validate: "blur" })

    // Either step failing starts over from empty credentials.
    const startOver = (message: string) => {
        reset(credentialsForm)
        reset(totpForm)
        setCredentials(null)
        setError({ message, open: true })
    }

    const login = async (submitted: Credentials, code = "") => {
        const result = await vm.login(submitted.username, submitted.password, code)

        switch (result.status) {
            case LoginStatus.Success:
                // Reload rather than navigate, so the app initialises with the new session.
                forceHomeRedirect()
                break

            case LoginStatus.TotpRequired:
                setCredentials(submitted)
                break

            case LoginStatus.Error:
                startOver(result.message)
                break
        }
    }

    return (
        <div className={styles.root}>
            <div className={styles.container}>
                <LockOpenIcon className={styles.lock} />
                <TriangleAlertIcon className={styles.warning} />
                <Card className={styles.card}>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <IapLogo aria-hidden="true" className="size-7.5 shrink-0" />
                            <h1 className="m-0 text-2xl leading-6 font-bold uppercase">In-App Protection</h1>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {credentials ? (
                            <TotpForm
                                form={totpForm}
                                onSubmit={(code) => login(credentials, code)}
                                onInput={closeError}
                            />
                        ) : (
                            <CredentialsForm
                                form={credentialsForm}
                                onSubmit={(submitted) => login(submitted)}
                                onInput={closeError}
                            />
                        )}
                        <Collapsible open={error.open}>
                            <CollapsibleContent className={styles.error}>
                                <Alert variant="destructive" className="border-destructive/10 bg-destructive/5">
                                    <CircleAlertIcon />
                                    <AlertTitle>{error.message}</AlertTitle>
                                </Alert>
                            </CollapsibleContent>
                        </Collapsible>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

interface CredentialsFormProps {
    /**
     * Owned by the view, which resets it when an attempt fails.
     */
    form: FormStore<typeof CredentialsSchema>
    /**
     * Called with the typed credentials once they pass the schema.
     */
    onSubmit: (credentials: Credentials) => void
    /**
     * Called on every keystroke, so the view can clear the error alert.
     */
    onInput: () => void
}

/**
 * The first login step, where the user types a username and a password. It is
 * also the step the view returns to after a failed attempt.
 */
const CredentialsForm = ({ form, onSubmit, onInput }: CredentialsFormProps) => (
    <Form of={form} onSubmit={onSubmit} onInput={onInput}>
        <FieldGroup>
            <LoginField form={form} path={["username"]} label="Username" autoFocus />
            <LoginField form={form} path={["password"]} label="Password" password />
            <LoginButton busy={form.isSubmitting} disabled={!v.is(CredentialsSchema, getInput(form))} />
        </FieldGroup>
    </Form>
)

interface TotpFormProps {
    /**
     * Owned by the view, which resets it when an attempt fails.
     */
    form: FormStore<typeof TotpSchema>
    /**
     * Called with the typed code once it passes the schema.
     */
    onSubmit: (code: string) => void
    /**
     * Called on every keystroke, so the view can clear the error alert.
     */
    onInput: () => void
}

/**
 * The second login step, shown when the server asks for a TOTP code. The
 * credentials it belongs to are held by the view, which sends them again with
 * the code.
 */
const TotpForm = ({ form, onSubmit, onInput }: TotpFormProps) => (
    <Form of={form} onSubmit={({ code }) => onSubmit(code)} onInput={onInput}>
        <FieldGroup>
            <LoginField form={form} path={["code"]} label="TOTP Code" />
            <LoginButton busy={form.isSubmitting} disabled={!v.is(TotpSchema, getInput(form))} />
        </FieldGroup>
    </Form>
)

interface LoginFieldProps<TSchema extends FormSchema, TPath extends RequiredPath> {
    /**
     * Form state owned by the login step.
     */
    form: FormStore<TSchema>
    /**
     * Schema field this input edits; also used to derive its id.
     */
    path: ValidPath<v.InferInput<TSchema>, TPath>
    /**
     * Label displayed above the input.
     */
    label: string
    /**
     * Masks the input and adds a button to reveal its value.
     */
    password?: boolean
    /**
     * Focuses the input when it appears.
     */
    autoFocus?: boolean
}

/**
 * Renders a labelled input for a login step, generic over the step's schema.
 *
 * Errors are shown as the input's placeholder instead of a `FieldError`; this
 * is specific to the login form (docs/adr/0004).
 */
const LoginField = <TSchema extends FormSchema, TPath extends RequiredPath>({
    form,
    path,
    label,
    password = false,
    autoFocus = false
}: LoginFieldProps<TSchema, TPath>) => {
    const id = `login-${path.join("-")}`

    return (
        <FormField of={form} path={path}>
            {(field) => {
                const invalid = isFieldInvalid(field)

                // The schema is generic here, so Formisch cannot narrow the input type.
                // All login fields contain strings.
                const value = String(field.input ?? "")

                return (
                    <Field data-invalid={invalid}>
                        <FieldLabel htmlFor={id}>{label}</FieldLabel>
                        {password ? (
                            <PasswordInput
                                {...field.props}
                                id={id}
                                value={value}
                                placeholder={field.errors?.[0]}
                                aria-invalid={invalid}
                                className="h-12"
                            />
                        ) : (
                            <Input
                                {...field.props}
                                id={id}
                                type="text"
                                value={value}
                                placeholder={field.errors?.[0]}
                                aria-invalid={invalid}
                                autoFocus={autoFocus}
                                className="h-12"
                            />
                        )}
                    </Field>
                )
            }}
        </FormField>
    )
}

type LoginButtonProps = Pick<ComponentProps<typeof BusyButton>, "busy" | "disabled">

/**
 * Submit button shared by both login steps.
 * It stays disabled until the form is valid and while the submission is running.
 */
const LoginButton = (props: LoginButtonProps) => (
    <BusyButton {...props} type="submit" className="mt-3 h-12 text-lg font-semibold uppercase">
        Login
    </BusyButton>
)
